import { useMemo } from "react";
import { FormatNumber } from "utils";
import { useFormatCurrency } from "utils/customHooks/useFormatCurrency";

import type { Currency } from "@krea/shared/types/common";
import { formatLenderMonthlyInterestRate } from "@krea/shared/utils/formatNumber";

import { useGetCurrentLenderCompanyQuery } from "@/features/lender-companies/hooks/use-get-lender-company.query";
import type { IRevolvingCreditBid } from "@/features/lender-responses/types/lender-response";

import type { IBidDetailsResponse } from "../../BidDetails";
import { BidDetailsListItem, EmptyField } from "../../bidHelpers";

interface IRevolvingCreditBidProps {
  bid: IRevolvingCreditBid;
  currency: Currency;
}

export const RevolvingCreditBid = ({
  bid,
  currency,
}: IRevolvingCreditBidProps): IBidDetailsResponse => {
  const [formattedCurrencyValue] = useFormatCurrency({ currency });

  const { data: lenderCompany } = useGetCurrentLenderCompanyQuery();
  const isInterestRatePerYearActivated =
    lenderCompany?.isFeatureInterestRatePerYearActivated;

  const averageMonthlyCost = bid.averageMonthlyCost;
  const interestMonthly = bid.comparableMonthlyInterestRate;
  const averageMonthlyPayment = bid.averageMonthlyPayment;
  const details = useMemo(() => {
    return (
      <div>
        {!!bid.amount && (
          <BidDetailsListItem
            title="Amount"
            value={formattedCurrencyValue(bid.amount)}
          />
        )}

        {!!bid.lenderMonthlyInterest && (
          <BidDetailsListItem
            title={
              isInterestRatePerYearActivated
                ? "Interest (per year)"
                : "Interest (per month)"
            }
            value={`${formatLenderMonthlyInterestRate(bid.lenderMonthlyInterest, "TO_YEARLY", isInterestRatePerYearActivated)}% / ${isInterestRatePerYearActivated ? "year" : "30 days"}`}
          />
        )}

        {!!averageMonthlyCost && (
          <BidDetailsListItem
            title="Interest payment"
            value={formattedCurrencyValue(averageMonthlyCost)}
          />
        )}
        {(!!bid.withdrawalFee || bid.withdrawalFeeAmount != null) && (
          <BidDetailsListItem
            title="Withdrawal fee"
            value={
              bid.withdrawalFeeAmount != null
                ? formattedCurrencyValue(bid.withdrawalFeeAmount)
                : `${bid.withdrawalFee}%`
            }
          />
        )}
        {!!bid.limitFeePerYear && (
          <BidDetailsListItem
            title="Limit fee (per year)"
            value={`${bid.limitFeePerYear}%`}
          />
        )}
        {!!bid.averageMonthlyAmortisation && (
          <BidDetailsListItem
            title="Principal repayment"
            value={formattedCurrencyValue(bid.averageMonthlyAmortisation)}
          />
        )}

        {!!bid.setupFee && (
          <BidDetailsListItem
            title="Setup fee"
            value={formattedCurrencyValue(bid.setupFee)}
          />
        )}

        {!!bid.administrationFees && (
          <BidDetailsListItem
            title="Administration fees (per month)"
            value={formattedCurrencyValue(bid.administrationFees)}
          />
        )}
      </div>
    );
  }, [
    bid,
    formattedCurrencyValue,
    averageMonthlyCost,
    isInterestRatePerYearActivated,
  ]);

  const summary = useMemo(() => {
    return (
      <div className="flex w-full flex-col gap-2 xl:grid xl:grid-cols-4 xl:gap-4">
        <div className="flex flex-col gap-4">
          <BidDetailsListItem
            variant="summary"
            title="Amount"
            value={
              bid.amount ? formattedCurrencyValue(bid.amount) : <EmptyField />
            }
          />
        </div>
        <div className="flex flex-col gap-4">
          <BidDetailsListItem
            variant="summary"
            title="Payment (at 50% utilization rate)"
            value={
              averageMonthlyPayment ? (
                formattedCurrencyValue(averageMonthlyPayment)
              ) : (
                <EmptyField />
              )
            }
          />
        </div>

        <div className="flex flex-col gap-4">
          <BidDetailsListItem
            variant="summary"
            title="Amortisation (per month)"
            value={
              bid.monthlyAmortisation ? (
                `${FormatNumber(bid.monthlyAmortisation)}%`
              ) : (
                <EmptyField />
              )
            }
          />
        </div>

        <div className="flex flex-col gap-4">
          <BidDetailsListItem
            variant="summary"
            title="Effective monthly interest"
            value={
              interestMonthly ? (
                `${FormatNumber(interestMonthly)}%`
              ) : (
                <EmptyField />
              )
            }
          />
        </div>
      </div>
    );
  }, [bid, formattedCurrencyValue, averageMonthlyPayment, interestMonthly]);

  return {
    summary,
    details,
  };
};
